/**
 * Floating combat numbers — `-24` where a blow lands, `+18` where a heal does.
 *
 * Janeks: *"show damage, received and dealt as floating number with `-`
 * beforehand. Same with healing, show +XX on player when healing, even if auto
 * regenerate. On Enemies, just show 2x separately floating `+` symbols."*
 *
 * ─── Borrowed from two existing systems, deliberately not from one ──────────
 * The POOL is `sprite-fx.ts`'s `createMuzzleFlashPool` shape: a fixed ring of
 * pre-built sprites, a cursor, a per-slot `remaining`, and an `update(dt)` that
 * hides expired ones. Nothing is allocated mid-fight, which matters when a
 * burst weapon can land six numbers in a second.
 *
 * The TEXT is `name-tags.ts`'s trick: bake an SVG string, hand it to the
 * texture loader as a data URI. That keeps the glyph rendering in one
 * well-understood place and — because `numberCard` is pure — makes it testable
 * without a canvas or a GPU.
 *
 * What is emphatically NOT reused is sprite-fx's MATERIALS. Every one of them
 * is `AdditiveBlending` with `fog: false`, which is right for a muzzle flash and
 * wrong for text: additive white-on-bright is unreadable over pale ground, and
 * a damage number must be legible over anything. These use normal blending.
 *
 * ─── Why they rise and fade rather than just appearing ──────────────────────
 * Two hits landing on the same body a few hundred milliseconds apart would draw
 * one number directly on top of the other and read as a single hit. The rise
 * separates them in time, and each slot gets a small deterministic horizontal
 * drift keyed off its own index so a rapid burst fans out instead of stacking
 * into one column. Deterministic, not random — `sprite-fx.ts` makes the same
 * choice for the same reason: a test can then assert placement.
 */
import * as THREE from 'three';

export type FloatingTone = 'damage-taken' | 'damage-dealt' | 'heal';

export interface FloatingSpawn {
  text: string;
  tone: FloatingTone;
  /** Crits read heavier — larger and brighter, not a different colour. */
  emphasis: boolean;
  x: number;
  y: number;
  z: number;
}

/** How long one number lives, seconds. */
const LIFETIME = 1.15;

/** Metres it climbs over its lifetime. */
const RISE = 1.0;

/** Metres of sideways drift, so a burst fans out instead of stacking. */
const DRIFT = 0.55;

/**
 * Slots in the pool.
 *
 * Twelve covers a burst from both duelists at once with room to spare. When it
 * is exhausted the oldest slot is recycled — dropping the NEWEST number would
 * be worse: the most recent hit is the one the player is trying to read.
 */
const POOL_SIZE = 12;

const CARD_W = 128;
const CARD_H = 64;

/** World size of one card, metres. Numbers stay legible without dwarfing a body. */
const SPRITE_W = 1.1;
const SPRITE_H = 0.55;

const TONE_FILL: Record<FloatingTone, string> = {
  // Incoming damage is the loudest thing on screen — it is the one number a
  // player must never miss.
  'damage-taken': '#ff6b5e',
  // Damage you deal is confirmation, not alarm: bone, not red.
  'damage-dealt': '#f2e4cf',
  heal: '#8fdc9b',
};

const TONE_STROKE: Record<FloatingTone, string> = {
  'damage-taken': '#2a0d0a',
  'damage-dealt': '#241c12',
  heal: '#0d2a13',
};

/**
 * One number as an SVG data URI.
 *
 * Pure and exported so the markup can be asserted directly — the escaping below
 * is the kind of thing that silently produces a blank texture rather than an
 * error.
 */
export function numberCard(text: string, tone: FloatingTone, emphasis: boolean): string {
  const size = emphasis ? 46 : 34;
  const fill = TONE_FILL[tone];
  const stroke = TONE_STROKE[tone];
  // The text is server-derived ("-24", "+ +"), never player-authored, but it
  // lands in markup — escape it anyway rather than relying on that staying true.
  const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${CARD_W}" height="${CARD_H}" viewBox="0 0 ${CARD_W} ${CARD_H}">`
    + `<text x="${CARD_W / 2}" y="${CARD_H / 2}" text-anchor="middle" dominant-baseline="central" `
    + `font-family="'Share Tech Mono','Courier New',monospace" font-size="${size}" font-weight="700" `
    // Painted as a thick stroke UNDER the fill (paint-order), which is what
    // keeps a pale number readable against pale ground without a boxed
    // background behind it — the Salvage Codex rule for item art applies to
    // this too.
    + `paint-order="stroke" stroke="${stroke}" stroke-width="6" stroke-linejoin="round" fill="${fill}">${safe}</text>`
    + '</svg>';
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

interface Slot {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  /** Seconds left. 0 = free. */
  remaining: number;
  /** Where it started, so the rise is absolute rather than accumulated per frame. */
  origin: THREE.Vector3;
  drift: number;
  scale: number;
}

export interface FloatingNumbersHandles {
  object: THREE.Object3D;
  spawn(spec: FloatingSpawn): void;
  update(dt: number, camera?: THREE.Camera): void;
  /** Live slot count — test seam, and cheap enough to assert in one. */
  activeCount(): number;
  dispose(): void;
}

export function createFloatingNumbers(poolSize = POOL_SIZE): FloatingNumbersHandles {
  const group = new THREE.Group();
  // Above the name tags (19) and the quest markers (20): a number that is
  // occluded by the label it is floating next to is a number nobody reads.
  group.renderOrder = 21;

  const loader = new THREE.TextureLoader();
  const slots: Slot[] = [];
  let cursor = 0;

  for (let i = 0; i < poolSize; i += 1) {
    const material = new THREE.SpriteMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      fog: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.visible = false;
    sprite.scale.set(SPRITE_W, SPRITE_H, 1);
    sprite.renderOrder = 21;
    group.add(sprite);
    slots.push({
      sprite,
      material,
      remaining: 0,
      origin: new THREE.Vector3(),
      // Deterministic per slot, fanning alternately left and right so
      // consecutive numbers separate rather than overlap.
      drift: (i % 2 === 0 ? 1 : -1) * DRIFT * (0.4 + 0.6 * ((i % 5) / 4)),
      scale: 1,
    });
  }

  const pickSlot = (): Slot => {
    // Prefer a free slot; otherwise recycle the one closest to expiry, so a
    // burst never discards the newest number.
    const free = slots.find((s) => s.remaining <= 0);
    if (free) return free;
    let oldest = slots[0];
    for (const s of slots) if (s.remaining < oldest.remaining) oldest = s;
    cursor = (cursor + 1) % slots.length;
    return oldest;
  };

  return {
    object: group,

    spawn(spec: FloatingSpawn): void {
      const slot = pickSlot();
      const texture = loader.load(numberCard(spec.text, spec.tone, spec.emphasis));
      // Each card is its own one-off image; there is nothing to share and the
      // previous one must not leak.
      slot.material.map?.dispose();
      slot.material.map = texture;
      slot.material.opacity = 1;
      slot.material.needsUpdate = true;
      slot.origin.set(spec.x, spec.y, spec.z);
      slot.remaining = LIFETIME;
      slot.scale = spec.emphasis ? 1.35 : 1;
      slot.sprite.position.copy(slot.origin);
      slot.sprite.scale.set(SPRITE_W * slot.scale, SPRITE_H * slot.scale, 1);
      slot.sprite.visible = true;
    },

    update(dt: number): void {
      for (const slot of slots) {
        if (slot.remaining <= 0) continue;
        slot.remaining = Math.max(0, slot.remaining - dt);
        if (slot.remaining === 0) {
          slot.sprite.visible = false;
          continue;
        }
        // `t` runs 0 -> 1 across the lifetime.
        const t = 1 - slot.remaining / LIFETIME;
        slot.sprite.position.set(
          slot.origin.x + slot.drift * t,
          // Eased so it leaps away from the body and then settles, rather than
          // drifting up at a constant rate like a balloon.
          slot.origin.y + RISE * (1 - (1 - t) * (1 - t)),
          slot.origin.z,
        );
        // Hold full opacity for the first half, then fade — a number that
        // starts fading immediately is hard to read at exactly the moment it
        // matters.
        slot.material.opacity = t < 0.5 ? 1 : 1 - (t - 0.5) / 0.5;
      }
    },

    activeCount(): number {
      return slots.filter((s) => s.remaining > 0).length;
    },

    dispose(): void {
      for (const slot of slots) {
        slot.material.map?.dispose();
        slot.material.dispose();
      }
      group.clear();
    },
  };
}
